import { Fragment, ReactNode } from "react";
import t from "@/lang/lang";
import { Card, CardContent } from "@/shared/components/ui/card";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { Offer } from "../types/offer";
import { ScopeOfWork } from "../../scope-of-work/types/scope-of-work";
import { OfferScopeOfWorkAddDialogConfig } from "./offer-scope-of-work-add-dialog";
import { OfferScopeOfWorkToolbar } from "./offer-scope-of-work-toolbar";
import { useSelectedScopeOfWorkFromSearchParams } from "../../scope-of-work/hooks/use-selected-scope-of-work-from-search-params";
import { OfferScopeOfWorkCard } from "./offer-scope-of-work-card";
import { OfferScopeOfWorkPositionsTable } from "./offer-scope-of-work-positions-table";

/**
 * OfferScopeOfWorksPage manages the scope of works of the offer: select,
 * add and delete one, and edit the data and positions of the selected
 * one. The selection is kept in the URL, falling back to the first.
 * positions renders the positions of the selected one, by default as an
 * {@link OfferScopeOfWorkPositionsTable}.
 */
export function OfferScopeOfWorksPage({
  offer,
  addDialogConfig,
  positions = (o, scopeOfWork) => (
    <OfferScopeOfWorkPositionsTable offer={o} scopeOfWork={scopeOfWork} />
  ),
}: {
  offer: Offer;
  addDialogConfig: OfferScopeOfWorkAddDialogConfig;
  positions?: (offer: Offer, scopeOfWork: ScopeOfWork) => ReactNode;
}) {
  const disabled = isImmutable(offer);
  const { selectedId, select, selected } =
    useSelectedScopeOfWorkFromSearchParams(offer.scopeOfWork);

  return (
    <div className="grid grid-cols-1 gap-2">
      <OfferScopeOfWorkToolbar
        offer={offer}
        addDialogConfig={addDialogConfig}
        selectedId={selectedId}
        onSelect={select}
        disabled={disabled}
      />
      {selected ? (
        <>
          <Fragment key={`positions-${selected.id}`}>
            {positions(offer, selected)}
          </Fragment>
          <OfferScopeOfWorkCard
            key={`card-${selected.id}`}
            offer={offer}
            scopeOfWork={selected}
            disabled={disabled}
          />
        </>
      ) : (
        <Card>
          <CardContent className="text-muted-foreground py-6 text-center text-sm">
            {t("Noch keine Leistungen. Fügen Sie eine Leistung hinzu.")}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
